import React from 'react';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import SignaturePad from 'Docs/Components/SignaturePad';
import 'assets/styles/react-datepicker.css';
import styles from './layoff.module.scss';

const ISO_DATE = 'YYYY-MM-DD';

/**
 * Shared form pieces for the lay-off pages (superintendent, employee, review)
 */

export const YES_NO = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];
// N/A is a real answer: every security question has to be answered
export const NOT_APPLICABLE = { value: 'na', label: 'N/A' };

// Sections 1-5 of the discharge form, filled in by the superintendent (read-only at review)
export const EMPTY_SECTIONS = {
  reason: '',
  timesheets: '',
  rehire_eligible: '',
  rehire_date: '',
  rehire_conditions: '',
  gear: '',
  credit_card: '',
  driver_pin: '',
  key_fob: '',
  key_fob_number: '',
  alarm_codes: '',
  entry_codes: '',
};

/**
 * Turn null answers from the API into empty strings for controlled inputs
 */
export const withDefaults = (empty, values) =>
  Object.fromEntries(
    Object.keys(empty).map((key) => [key, values?.[key] ?? empty[key]]),
  );

/**
 * First message of each field from a 422 validation response
 */
export const firstErrors = (fieldErrors) =>
  Object.fromEntries(
    Object.entries(fieldErrors || {}).map(([key, messages]) => [
      key,
      messages[0],
    ]),
  );

/**
 * A refusal note has to be a sentence about what happened, not a single word
 * (mirrors RecordLayoffRefusalRequest)
 */
export const refusalNoteError = (note) => {
  const text = (note || '').trim();
  if (!text) return 'Add a note before recording the refusal.';
  if (text.length < 20 || text.split(/\s+/).length < 4)
    return 'Describe what happened in a full sentence (at least 20 characters).';
  return '';
};

/**
 * "*" after the label of a field that has to be filled in
 */
export const RequiredMark = () => (
  <span className={styles.required_mark} aria-hidden='true'>
    {' '}
    *
  </span>
);

/**
 * Date input shown and typed as MM/DD/YYYY whatever the browser's language (a native date
 * input follows the browser's locale). Values in and out are YYYY-MM-DD strings, '' when empty.
 * describedBy links the input to its hint or error message for screen readers.
 */
export const DateInput = ({
  id,
  value,
  onChange,
  min,
  max,
  invalid,
  describedBy,
}) => (
  <div className={styles.date_field}>
    <DatePicker
      id={id}
      // The picker copies its own props onto this input, so aria-describedby reaches it
      customInput={<input type='text' aria-describedby={describedBy} />}
      selected={value ? moment(value, ISO_DATE) : null}
      onChange={(date) => onChange(date ? date.format(ISO_DATE) : '')}
      dateFormat='MM/DD/YYYY'
      placeholderText='mm/dd/yyyy'
      minDate={min ? moment(min, ISO_DATE) : undefined}
      maxDate={max ? moment(max, ISO_DATE) : undefined}
      className={`${styles.input} ${invalid ? styles.invalid : ''}`}
      autoComplete='off'
      showMonthDropdown
      showYearDropdown
      dropdownMode='select'
    />
  </div>
);

export const FieldError = ({ id, message }) =>
  message ? (
    <span id={id} className={styles.error_text}>
      {message}
    </span>
  ) : null;

export const RadioGroup = ({
  name,
  legend,
  options,
  value,
  onChange,
  error,
  required = false,
}) => (
  <fieldset
    className={styles.radio_group}
    aria-describedby={error ? `${name}-error` : undefined}
    aria-required={required || undefined}
  >
    <legend className={styles.field_label}>
      {legend}
      {required && <RequiredMark />}
    </legend>
    <div className={styles.radio_options}>
      {options.map((option) => {
        const id = `${name}-${option.value || 'na'}`;
        return (
          <div key={id} className={styles.radio_option}>
            <input
              type='radio'
              id={id}
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
            />
            <label htmlFor={id}>{option.label}</label>
          </div>
        );
      })}
    </div>
    <FieldError id={`${name}-error`} message={error} />
  </fieldset>
);

export const TextField = ({
  id,
  label,
  value,
  onChange,
  error,
  type = 'text',
  required = false,
  ...inputProps
}) => (
  <div>
    <label htmlFor={id} className={styles.field_label}>
      {label}
      {required && <RequiredMark />}
    </label>
    {type === 'date' ? (
      <DateInput
        id={id}
        value={value}
        onChange={onChange}
        min={inputProps.min}
        max={inputProps.max}
        invalid={!!error}
      />
    ) : (
      <input
        id={id}
        type={type}
        className={`${styles.input} ${error ? styles.invalid : ''}`}
        value={value ?? ''}
        onChange={(event) => onChange(event.target.value)}
        aria-required={required || undefined}
        {...inputProps}
      />
    )}
    <FieldError message={error} />
  </div>
);

/**
 * Draw a signature, or reuse the one saved on the user's profile
 */
export const SignatureField = ({
  label,
  value,
  onChange,
  error,
  savedSignature,
  required = false,
}) => (
  <div className={styles.signature_block}>
    <span className={styles.field_label}>
      {label}
      {required && <RequiredMark />}
    </span>
    <SignaturePad value={value} onSave={onChange} error={!!error} />
    {savedSignature && value !== savedSignature && (
      <button
        type='button'
        className={`button outlined ${styles.small_button}`}
        onClick={() => onChange(savedSignature)}
      >
        Use my saved signature
      </button>
    )}
    <FieldError message={error} />
  </div>
);

/**
 * Sections 1-5 of the discharge form: reason, timesheets, rehire, gear, security measures.
 * `start` is the number shown on the first question.
 */
export const SupervisorSections = ({
  form,
  setField,
  errors = {},
  start = 1,
}) => (
  <>
    <div>
      <label htmlFor='reason' className={styles.field_label}>
        {start}. Reason for separation
        <RequiredMark />
      </label>
      <textarea
        id='reason'
        rows={3}
        maxLength={500}
        className={`${styles.textarea} ${errors['form.reason'] ? styles.invalid : ''}`}
        value={form.reason}
        onChange={(event) => setField('reason')(event.target.value)}
        placeholder='For example: end of the fire season'
      />
      <FieldError message={errors['form.reason']} />
    </div>

    <RadioGroup
      name='timesheets'
      legend={`${start + 1}. Timesheets`}
      options={[
        { value: 'all_turned_in', label: 'All turned in' },
        { value: 'pending', label: 'Pending' },
      ]}
      value={form.timesheets}
      onChange={setField('timesheets')}
      error={errors['form.timesheets']}
      required
    />

    <div>
      <RadioGroup
        name='rehire_eligible'
        legend={`${start + 2}. Eligible for rehire`}
        options={[
          { value: 'yes', label: 'Yes' },
          { value: 'conditional', label: 'Under conditions' },
          { value: 'no', label: 'No' },
        ]}
        value={form.rehire_eligible}
        onChange={setField('rehire_eligible')}
        error={errors['form.rehire_eligible']}
        required
      />
      {form.rehire_eligible === 'yes' && (
        <div className={styles.sub_field}>
          <TextField
            id='rehire-date'
            type='date'
            label='Estimated rehire date'
            value={form.rehire_date}
            onChange={setField('rehire_date')}
            error={errors['form.rehire_date']}
            required
          />
        </div>
      )}
      {form.rehire_eligible === 'conditional' && (
        <div className={styles.sub_field}>
          <TextField
            id='rehire-conditions'
            label='Conditions'
            maxLength={200}
            value={form.rehire_conditions}
            onChange={setField('rehire_conditions')}
            error={errors['form.rehire_conditions']}
            required
          />
        </div>
      )}
    </div>

    <RadioGroup
      name='gear'
      legend={`${start + 3}. Issued gear`}
      options={[
        { value: 'all', label: 'All returned' },
        { value: 'partial', label: 'Partially returned' },
        { value: 'none', label: 'None returned' },
      ]}
      value={form.gear}
      onChange={setField('gear')}
      error={errors['form.gear']}
      required
    />

    <fieldset className={styles.security_group}>
      <legend className={styles.field_label}>
        {start + 4}. Security measures
        <RequiredMark />
      </legend>
      <p className={styles.hint}>
        Answer every question. Choose N/A when the employee doesn&apos;t have
        the item. The superintendent&apos;s initials are added where a Yes is
        confirmed.
      </p>
      <RadioGroup
        name='credit_card'
        legend='Credit card returned or held for off-season work?'
        options={[
          { value: 'yes', label: 'Returned' },
          { value: 'hold', label: 'Held' },
          NOT_APPLICABLE,
        ]}
        value={form.credit_card}
        onChange={setField('credit_card')}
        error={errors['form.credit_card']}
        required
      />
      <RadioGroup
        name='driver_pin'
        legend='Driver pin cancelled or deactivated until re-hired?'
        options={[...YES_NO, NOT_APPLICABLE]}
        value={form.driver_pin}
        onChange={setField('driver_pin')}
        error={errors['form.driver_pin']}
        required
      />
      <RadioGroup
        name='key_fob'
        legend='Key fob cancelled or turned in until re-hired?'
        options={[...YES_NO, NOT_APPLICABLE]}
        value={form.key_fob}
        onChange={setField('key_fob')}
        error={errors['form.key_fob']}
        required
      />
      {(form.key_fob === 'yes' || form.key_fob === 'no') && (
        <div className={styles.sub_field}>
          <TextField
            id='key-fob-number'
            label='Key fob #'
            maxLength={20}
            value={form.key_fob_number}
            onChange={setField('key_fob_number')}
            error={errors['form.key_fob_number']}
            required
          />
        </div>
      )}
      <RadioGroup
        name='alarm_codes'
        legend='Alarm & security codes cancelled?'
        options={[...YES_NO, NOT_APPLICABLE]}
        value={form.alarm_codes}
        onChange={setField('alarm_codes')}
        error={errors['form.alarm_codes']}
        required
      />
      <RadioGroup
        name='entry_codes'
        legend='Entry codes changed?'
        options={[...YES_NO, NOT_APPLICABLE]}
        value={form.entry_codes}
        onChange={setField('entry_codes')}
        error={errors['form.entry_codes']}
        required
      />
    </fieldset>
  </>
);
